import { c as _c } from "react/compiler-runtime";
import { ChainBadge, ChainBadgeFragment, CollectionLockup, CollectionLockupAvatar, CollectionLockupContent, CollectionLockupFragment, CollectionLockupTitle } from "@opensea/app-kit";
import { graphql, readFragment, useMutation, useQuery } from "@opensea/graphql";
import { Avatar, Button, Link } from "@opensea/next-ui-kit";
import { FlexColumn, Input, Modal, ModalBody, ModalFooter, ModalHeader, ModalHeaderTitle, RadioGroup, TextBody, UnstyledButton } from "@opensea/ui-kit";
import { ChevronLeft, ChevronRight, Search } from "@opensea/ui-kit/icons";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { CollectionPreviewTooltip } from "../collection/CollectionPreviewTooltip/CollectionPreviewTooltip";
import { CollectionPreviewTooltipFragment } from "../collection/CollectionPreviewTooltip/CollectionPreviewTooltip.fragment";
import { EntityType } from "./types";
const createUserReportMutation = graphql(`
  mutation CreateUserReport(
    $collectionSlug: String
    $itemIdentifier: ItemIdentifierInput
    $accountAddress: Address
    $category: String!
    $subCategory: String
  ) {
    createUserReport(
      identifier: {
        collectionSlug: $collectionSlug
        itemIdentifier: $itemIdentifier
        accountAddress: $accountAddress
      }
      category: $category
      subCategory: $subCategory
    )
  }
`);
const ReportModal_collectionFragment = graphql(`
    fragment ReportModal_collection on Collection {
      id
      slug
      name
      imageUrl
      chain {
        ...ChainBadge
      }
      ...CollectionLockup
      ...CollectionPreviewTooltip
    }
  `, [ChainBadgeFragment, CollectionLockupFragment, CollectionPreviewTooltipFragment]);
const ReportModal_accountFragment = graphql(`
  fragment ReportModal_account on Profile {
    address
    displayName
    imageUrl
  }
`);
const ReportModal_itemFragment = graphql(`
  fragment ReportModal_item on Item {
    id
    name
    imageUrl
  }
`);
const useReportSearchResults = (query, entityType) => {
  const $ = _c(22);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      suspense: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const context = t0;
  const pause = query.trim().length === 0;
  let t1;
  if ($[1] !== entityType) {
    t1 = function getSearchQuery() {
      switch (entityType) {
        case EntityType.ACCOUNT:
          {
            return graphql("\n            query ReportModalAccountSearch($query: String!, $limit: Int) {\n              accountsByQuery(query: $query, limit: $limit) {\n                ...ReportModal_account\n              }\n            }\n          ", [ReportModal_accountFragment]);
          }
        case EntityType.ITEM:
          {
            return graphql("\n            query ReportModalItemSearch($query: String!, $limit: Int) {\n              itemsByQuery(query: $query, limit: $limit) {\n                ...ReportModal_item\n              }\n            }\n          ", [ReportModal_itemFragment]);
          }
        default:
          {
            return graphql("\n            query ReportModalCollectionSearch($query: String!, $limit: Int) {\n              collectionsByQuery(query: $query, limit: $limit) {\n                ...ReportModal_collection\n              }\n            }\n          ", [ReportModal_collectionFragment]);
          }
      }
    };
    $[1] = entityType;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const getSearchQuery = t1;
  let t2;
  if ($[3] !== getSearchQuery) {
    t2 = getSearchQuery();
    $[3] = getSearchQuery;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== query) {
    t3 = {
      query,
      limit: 4
    };
    $[5] = query;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== pause || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      query: t2,
      variables: t3,
      pause,
      context
    };
    $[7] = pause;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const [t5] = useQuery(t4);
  const {
    data,
    error,
    fetching
  } = t5;
  let t6;
  if ($[11] !== data?.accountsByQuery || $[12] !== data?.collectionsByQuery || $[13] !== data?.itemsByQuery || $[14] !== entityType) {
    t6 = function getResults() {
      switch (entityType) {
        case EntityType.ACCOUNT:
          {
            const accounts = data?.accountsByQuery || [];
            return accounts.map(_temp);
          }
        case EntityType.ITEM:
          {
            const items = data?.itemsByQuery || [];
            return items.map(_temp2);
          }
        default:
          {
            const collections = data?.collectionsByQuery || [];
            return collections.map(_temp3);
          }
      }
    };
    $[11] = data?.accountsByQuery;
    $[12] = data?.collectionsByQuery;
    $[13] = data?.itemsByQuery;
    $[14] = entityType;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const getResults = t6;
  const dedupeResults = _temp4;
  let t7;
  if ($[16] !== getResults) {
    t7 = dedupeResults(getResults());
    $[16] = getResults;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== error || $[19] !== fetching || $[20] !== t7) {
    t8 = {
      searchResults: t7,
      error,
      fetching
    };
    $[18] = error;
    $[19] = fetching;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};

// Update SearchResultItem type

export function ReportModal({
  open,
  onOpenChange,
  entityType,
  entityId
}) {
  const [{
    error: mutationError
  }, createReport] = useMutation(createUserReportMutation);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [isItemSelected, setIsItemSelected] = useState(false);
  const t = useTranslations("ReportModal");
  useEffect(() => {
    if (!open) {
      setIsSubmitted(false);
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setSearchQuery("");
      setError(null);
      setSearchInputValue("");
      setDebouncedSearchQuery("");
      setIsItemSelected(false);
    }
  }, [open]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchInputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInputValue]);
  const {
    searchResults: results
  } = useReportSearchResults(debouncedSearchQuery, entityType);
  const filteredResults = isItemSelected ? [] : results;
  const handleClose = () => {
    onOpenChange(false);
    setIsSubmitted(false);
    setSelectedCategory(null);
    setSelectedSubcategory(null);
  };
  const adultContentSubcategories = [{
    key: "adult_content_nudity",
    title: "Nudity or sexual content",
    context: "Nudity, sexual scenes or language, or sex trafficking.",
    learnMoreUrl: "https://support.opensea.io/articles/8867128-is-explicit-sensitive-content-allowed-on-opensea"
  }, {
    key: "adult_content_shocking_gory",
    title: "Shocking or gory",
    context: "Shocking or graphic content.",
    learnMoreUrl: "https://support.opensea.io/articles/8867128-is-explicit-sensitive-content-allowed-on-opensea"
  }, {
    key: "adult_content_sexual_harassment",
    title: "Sexual harassment",
    context: "Unwanted romantic advances, requests for sexual favors, or unwelcome sexual remarks.",
    learnMoreUrl: "https://support.opensea.io/articles/8867128-is-explicit-sensitive-content-allowed-on-opensea"
  }];
  const replaceLinksInText = (text, links) => {
    if (!text) {
      return text;
    }
    let result = text;

    // First apply any custom link replacements
    if (links) {
      links.forEach(link => {
        result = result.replace(link.text, `<a href="${link.url}" class="hover:opacity-70 text-text-primary" target="_blank" rel="noopener noreferrer">${link.text}</a>`);
      });
    }

    // Then apply default Help Center link if not already replaced
    if (result.includes("Help Center") && !result.includes("href=") && !result.includes("Help Center</a>")) {
      result = result.replace("Help Center", `<a href="https://support.opensea.io" class="hover:opacity-70 text-text-primary" target="_blank" rel="noopener noreferrer">Help Center</a>`);
    }
    return <span
    // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized content
    dangerouslySetInnerHTML={{
      __html: result
    }} />;
  };
  const reportOptions = [{
    key: "fake_misleading",
    title: "Fake or misleading",
    onClick: () => {
      setSelectedCategory("fake_misleading");
    }
  }, {
    key: "scam_malicious",
    title: "Scam or malicious behavior",
    onClick: () => {
      setIsSubmitted(true);
    }
  }, {
    key: "hate_violence",
    title: "Inciting hate or violence",
    onClick: () => {
      setIsSubmitted(true);
    }
  }, {
    key: "adult_content",
    title: "Explicit and sensitive content",
    onClick: () => {
      setSelectedCategory("adult_content");
    }
  }, {
    key: "ip_infringement",
    title: "Intellectual property infringement",
    onClick: () => {
      setSelectedCategory("ip_infringement");
    },
    message: {
      text: "We do not allow content that violates intellectual property rights on OpenSea. In accordance with U.S. federal law, we maintain a notice and takedown process for alleged intellectual property infringement. Learn more.",
      secondaryText: "If you'd like to file a takedown request, please review the OpenSea Help Center for more information.",
      links: [{
        text: "Learn more",
        url: "https://support.opensea.io/articles/8867068-what-can-i-do-if-my-copyrighted-or-trademarked-works-are-being-sold-without-my-permission"
      }]
    }
  }, ...(entityType === EntityType.ITEM ? [{
    key: "stolen_item",
    title: "Stolen item or funds",
    onClick: () => {
      setSelectedCategory("stolen_item");
    },
    message: {
      text: "We do not allow the sale of stolen items on OpenSea. When an item is reported as stolen, we require a verified police report in order to disable the ability to buy, sell, or transfer items. Learn more.",
      secondaryText: "If you'd like to report an item as stolen, please contact our support team on the OpenSea Help Center.",
      links: [{
        text: "Learn more",
        url: "https://support.opensea.io/articles/8867064-what-is-opensea-s-stolen-item-policy"
      }]
    }
  }] : []), ...(entityType === EntityType.COLLECTION ? [{
    key: "spam",
    title: "Spam",
    onClick: () => {
      setIsSubmitted(true);
    }
  }] : []), {
    key: "other",
    title: "Other",
    onClick: () => {
      setSelectedCategory("other");
    },
    message: {
      text: "For other issues, please review the OpenSea Help Center and contact our support team.",
      links: [] // No custom links needed since Help Center will be auto-linked
    }
  }];
  const handleDone = () => {
    handleClose();
  };
  const handleReport = async (reason, reportEntityType, reportEntityId, subcategory) => {
    try {
      const variables = {
        category: reason,
        subCategory: subcategory?.key || ""
      };
      switch (reportEntityType) {
        case EntityType.COLLECTION:
          variables.collectionSlug = reportEntityId;
          break;
        case EntityType.ITEM:
          if (typeof reportEntityId === "string") {
            variables.itemIdentifier = {
              chain: "",
              contractAddress: "",
              tokenId: reportEntityId
            };
          } else {
            variables.itemIdentifier = {
              chain: reportEntityId.chain,
              contractAddress: reportEntityId.contractAddress,
              tokenId: reportEntityId.tokenId
            };
          }
          break;
        case EntityType.ACCOUNT:
          variables.accountAddress = reportEntityId.toLowerCase();
          break;
        default:
          throw new Error(`Invalid entity type: ${String(reportEntityType)}`);
      }
      const result_0 = await createReport(variables);
      if (result_0.error) {
        throw result_0.error;
      }
      if (!result_0.data?.createUserReport) {
        throw new Error("Failed to submit report. Please try again later.");
      }
      return result_0.data.createUserReport;
    } catch (err) {
      console.error("Error submitting report:", err);
      if (err instanceof Error) {
        throw new Error(`Failed to submit report: ${err.message}`);
      }
      throw new Error("An unexpected error occurred while submitting the report");
    }
  };
  const getDisplayName = result_1 => {
    switch (result_1.type) {
      case "account":
        {
          const data_0 = result_1.data;
          return data_0.displayName || data_0.address;
        }
      case "collection":
        {
          const collection = result_1.data;
          return collection.name;
        }
      case "item":
        {
          const data = result_1.data;
          return data.name;
        }
      default:
        return "Unknown";
    }
  };
  const getImageUrl = result_2 => {
    switch (result_2.type) {
      case "account":
        {
          const data_2 = result_2.data;
          return data_2.imageUrl;
        }
      case "collection":
        {
          const collection_0 = result_2.data;
          return collection_0.imageUrl;
        }
      case "item":
        {
          const data_1 = result_2.data;
          return data_1.imageUrl;
        }
      default:
        return null;
    }
  };
  const getResultId = result_3 => {
    switch (result_3.type) {
      case "account":
        {
          const data_4 = result_3.data;
          return data_4.address;
        }
      case "collection":
        {
          const collection_1 = result_3.data;
          return collection_1.slug;
        }
      case "item":
        {
          const data_3 = result_3.data;
          return data_3.id;
        }
      default:
        return result_3.id;
    }
  };
  useEffect(() => {
    if (mutationError) {
      setError(mutationError.message);
      setIsSubmitted(true);
    }
  }, [mutationError]);
  const headerButton = () => {
    if (!selectedCategory) {
      return null;
    }
    if (selectedCategory === "adult_content") {
      return null;
    }
    return <UnstyledButton aria-describedby="report-description" className="mr-4 text-text-secondary hover:text-text-primary" onClick={() => setSelectedCategory(null)}>
        <ChevronLeft />
      </UnstyledButton>;
  };
  const handleReportClick = async option => {
    if (["scam_malicious", "hate_violence", "spam"].includes(option.key)) {
      try {
        await handleReport(option.key, entityType, entityId);
        if (!error) {
          option.onClick();
        }
      } catch (err_0) {
        console.error(err_0);
      }
    } else {
      option.onClick();
    }
  };
  const getModalContent = () => {
    if (isSubmitted) {
      return <>
          <ModalHeader>
            <ModalHeaderTitle id="submitted-modal-title">
              {error ? t("error") : t("thankYou")}
            </ModalHeaderTitle>
          </ModalHeader>
          <ModalBody aria-describedby="submitted-description">
            <FlexColumn className="gap-4">
              {error ? <TextBody className="text-text-secondary">
                  {t("errorOccurred")}
                </TextBody> : <>
                  <TextBody className="font-semibold" id="submitted-description">
                    {t("whatHappensNow")}
                  </TextBody>
                  <TextBody className="text-text-secondary">
                    {t("teamWillReview")}
                  </TextBody>
                </>}
            </FlexColumn>
          </ModalBody>
          <ModalFooter>
            <Button aria-describedby="submitted-description" className="w-full justify-center py-2 hover:opacity-70" onClick={handleDone}>
              {t("done")}
            </Button>
          </ModalFooter>
        </>;
    }
    const renderCategoryContent = () => {
      switch (selectedCategory) {
        case "adult_content":
          return <>
              <TextBody className="text-text-secondary">
                {t("whatTypeAdultContent")}
              </TextBody>
              <RadioGroup onValueChange={value => setSelectedSubcategory(value)} value={selectedSubcategory || undefined}>
                <FlexColumn className="gap-4">
                  {adultContentSubcategories.map(subcategory_0 => <div key={subcategory_0.key}>
                      <div className="flex items-center gap-2">
                        <input checked={selectedSubcategory === subcategory_0.key} className="size-4" id={subcategory_0.key} onChange={e_0 => setSelectedSubcategory(e_0.target.value)} type="radio" value={subcategory_0.key} />
                        <label htmlFor={subcategory_0.key}>
                          <TextBody>{subcategory_0.title}</TextBody>
                        </label>
                      </div>
                      {selectedSubcategory === subcategory_0.key && <div className="mt-2 ml-6">
                          <TextBody className="text-sm text-text-secondary">
                            {subcategory_0.context}
                          </TextBody>
                          <div className="h-2" />
                          {subcategory_0.learnMoreUrl ? <TextBody className="mt-1 text-sm text-text-secondary">
                              <Link className="text-text-primary hover:opacity-70" href={subcategory_0.learnMoreUrl}>
                                {t("learnMore")}
                              </Link>
                            </TextBody> : null}
                        </div>}
                    </div>)}
                </FlexColumn>
              </RadioGroup>
              <Button className="mt-4" disabled={!selectedSubcategory} onClick={() => {
              if (selectedSubcategory) {
                handleReport(selectedCategory, entityType, entityId, adultContentSubcategories.find(s => s.key === selectedSubcategory)).then(() => {
                  setIsSubmitted(true);
                }).catch(() => {
                  setError("submission_failed");
                  setIsSubmitted(true);
                });
              }
            }}>
                {t("submit")}
              </Button>
            </>;
        case "ip_infringement":
          return <>
              <TextBody className="text-text-secondary">
                {replaceLinksInText(reportOptions.find(opt_5 => opt_5.key === selectedCategory)?.message?.text || "", reportOptions.find(opt_6 => opt_6.key === selectedCategory)?.message?.links)}
              </TextBody>
              <TextBody className="text-text-secondary">
                {replaceLinksInText(reportOptions.find(opt_7 => opt_7.key === selectedCategory)?.message?.secondaryText || "", reportOptions.find(opt_8 => opt_8.key === selectedCategory)?.message?.links)}
              </TextBody>
              <Button className="mt-4" onClick={handleClose}>
                {t("done")}
              </Button>
            </>;
        case "stolen_item":
          return <>
              <TextBody className="text-text-secondary">
                {replaceLinksInText(reportOptions.find(opt_1 => opt_1.key === selectedCategory)?.message?.text || "", reportOptions.find(opt_2 => opt_2.key === selectedCategory)?.message?.links)}
              </TextBody>
              <TextBody className="text-text-secondary">
                {replaceLinksInText(reportOptions.find(opt_3 => opt_3.key === selectedCategory)?.message?.secondaryText || "", reportOptions.find(opt_4 => opt_4.key === selectedCategory)?.message?.links)}
              </TextBody>
              <Button className="mt-4" onClick={handleClose}>
                {t("done")}
              </Button>
            </>;
        case "other":
          return <>
              <TextBody className="text-text-secondary">
                {replaceLinksInText(reportOptions.find(opt => opt.key === selectedCategory)?.message?.text || "", reportOptions.find(opt_0 => opt_0.key === selectedCategory)?.message?.links)}
              </TextBody>
              <Button className="mt-4" onClick={handleClose}>
                {t("done")}
              </Button>
            </>;
        case "fake_misleading":
          return <>
              <TextBody className="text-text-secondary">
                {t("searchForOriginal", {
                entityType: entityType.toLowerCase()
              })}
              </TextBody>
              <Input leftIcon={Search} onChange={e => {
              setSearchInputValue(e.target.value);
              setSearchQuery("");
              setIsItemSelected(false);
            }} placeholder={t("searchFor", {
              entityType: entityType.toLowerCase()
            })} value={searchInputValue} />
              {filteredResults.length > 0 && !isItemSelected && <div className="mt-4 max-h-[300px] overflow-y-auto">
                  {filteredResults.map(result_4 => {
                if (result_4.type !== "collection") {
                  return <UnstyledButton className="flex w-full items-center gap-3 rounded-lg p-3 hover:bg-bg-additional-1" key={result_4.id} onClick={() => {
                    setSearchQuery(getResultId(result_4));
                    setSearchInputValue(getDisplayName(result_4));
                    setIsItemSelected(true);
                  }}>
                          <Avatar rounded size={24} src={getImageUrl(result_4) || undefined} />
                          <div className="flex-1">
                            <TextBody className="font-medium">
                              {getDisplayName(result_4)}
                            </TextBody>
                          </div>
                        </UnstyledButton>;
                }
                const collection_2 = readFragment(ReportModal_collectionFragment, result_4.data);
                return <UnstyledButton className="flex w-full items-center gap-3 rounded-lg p-3 hover:bg-bg-additional-1" key={result_4.id} onClick={() => {
                  setSearchQuery(getResultId(result_4));
                  setSearchInputValue(getDisplayName(result_4));
                  setIsItemSelected(true);
                }}>
                        <CollectionPreviewTooltip collection={collection_2}>
                          <CollectionLockup collection={collection_2} size="sm">
                            <CollectionLockupAvatar badge={<ChainBadge chain={collection_2.chain} size="xs" />} />
                            <CollectionLockupContent>
                              <CollectionLockupTitle disableTextOverflowTooltip />
                            </CollectionLockupContent>
                          </CollectionLockup>
                        </CollectionPreviewTooltip>
                      </UnstyledButton>;
              })}
                </div>}
              <Button className="mt-4" disabled={!searchQuery} onClick={async () => {
              if (searchQuery) {
                try {
                  await handleReport("fake_misleading", entityType, entityId);
                  setIsSubmitted(true);
                } catch {
                  setError("submission_failed");
                  setIsSubmitted(true);
                }
              }
            }}>
                {t("submitReport")}
              </Button>
            </>;
        default:
          return <>
              <TextBody className="font-semibold">
                {t("whyReporting", {
                entityType: entityType.toLowerCase()
              })}
              </TextBody>
              <TextBody className="text-text-secondary">
                {t("teamWillNotify")}
              </TextBody>
              {reportOptions.map(option_0 => <UnstyledButton className="flex w-full items-center justify-between py-2 hover:opacity-70" key={option_0.title} onClick={() => handleReportClick(option_0)}>
                  <TextBody>{option_0.title}</TextBody>
                  <ChevronRight className="text-text-secondary" />
                </UnstyledButton>)}
            </>;
      }
    };
    return <>
        <ModalHeader>
          <ModalHeaderTitle className="text-center" id="report-modal-title">
            <div className="flex w-full items-center">
              {headerButton()}
              <div className="flex-1 text-center">
                <TextBody className="font-semibold" id="report-description">
                  {t("report")}
                </TextBody>
              </div>
            </div>
          </ModalHeaderTitle>
        </ModalHeader>
        <ModalBody aria-describedby="report-description">
          <FlexColumn className="gap-4">{renderCategoryContent()}</FlexColumn>
        </ModalBody>
      </>;
  };
  return <Modal aria-labelledby={isSubmitted ? "submitted-modal-title" : "report-modal-title"} content={getModalContent()} onOpenChange={handleClose} open={open} title={t("report")} />;
}
function _temp(a) {
  const fragment_1 = readFragment(ReportModal_accountFragment, a);
  return {
    id: fragment_1.address,
    type: "account",
    data: fragment_1
  };
}
function _temp2(i) {
  const fragment_0 = readFragment(ReportModal_itemFragment, i);
  return {
    id: fragment_0.id,
    type: "item",
    data: fragment_0
  };
}
function _temp3(c) {
  const fragment = readFragment(ReportModal_collectionFragment, c);
  return {
    id: fragment.slug,
    type: "collection",
    data: fragment
  };
}
function _temp4(items_0) {
  const seen = new Set();
  return items_0.filter(item => {
    const key = `${item.type}:${item.id.toLowerCase()}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}